import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CalendarCheck, Compass, Cpu, Eye, FileText, HardHat, HeartHandshake, KeyRound, Landmark, MapPin, Search, ShieldCheck, Target, TrendingUp, Wrench } from "lucide-react";
import { useBi } from "../../lib/customer";
import { Reveal, SectionHead } from "../Common";
import { useNum } from "./Kit";

const SERVICES = [
  ["development", Landmark, "Development", "التطوير", "Residential, commercial and mixed-use projects, from planning to delivery.", "مشاريع سكنية وتجارية ومتعددة الاستخدامات من التخطيط حتى التسليم."],
  ["sales", KeyRound, "Sales", "المبيعات", "Marketing and sale of homes and commercial assets.", "تسويق وبيع المنازل والأصول التجارية."],
  ["leasing", FileText, "Leasing", "التأجير", "Homes, offices and retail spaces to lease.", "منازل ومكاتب ومحلات للإيجار."],
  ["property-management", Wrench, "Property Management", "إدارة الأملاك", "Tenant relations, maintenance coordination and asset care.", "علاقات المستأجرين وتنسيق الصيانة والعناية بالأصول."],
  ["investment-advisory", TrendingUp, "Investment Advisory", "الاستشارات الاستثمارية", "Land and income-generating opportunities for investors.", "فرص الأراضي والعقارات المدرة للدخل للمستثمرين."],
  ["off-plan", Compass, "Off-plan Sales", "البيع على الخارطة", "Reserve units in our upcoming developments before completion.", "احجز وحدتك في مشاريعنا القادمة قبل اكتمالها."],
];

const WHY = [
  [Target, "Vision 2030 aligned design", "تصميم يواكب رؤية 2030", "Communities planned around quality of life, sustainability and the Kingdom's long-term ambitions.", "مجتمعات مخططة حول جودة الحياة والاستدامة وطموحات المملكة بعيدة المدى."],
  [HardHat, "Quality construction", "جودة في التنفيذ", "Materials, contractors and site supervision chosen for durability and a lasting finish.", "مواد ومقاولون وإشراف موقعي يُختارون من أجل المتانة وجودة التشطيب."],
  [Eye, "Transparent process", "إجراءات واضحة", "Clear documentation and honest communication at every step, from enquiry to handover.", "وثائق واضحة وتواصل صادق في كل خطوة، من الاستفسار حتى التسليم."],
  [MapPin, "Prime locations", "مواقع مميزة", "Sites selected for access, daily convenience and long-term neighbourhood value.", "مواقع مختارة لسهولة الوصول وراحة الحياة اليومية وقيمة الحي على المدى الطويل."],
  [HeartHandshake, "After-sales care", "خدمة ما بعد البيع", "A dedicated team that stays with you after handover.", "فريق مخصص يبقى معك بعد التسليم."],
  [Cpu, "Smart-home ready", "جاهزية للمنزل الذكي", "Homes designed to welcome modern smart-home technology.", "منازل مصممة لاستيعاب تقنيات المنزل الذكي الحديثة."],
];

const STEPS = [
  [Search, "Discover", "اكتشف", "Search listings and developments by city, type and budget.", "ابحث في العقارات والمشاريع حسب المدينة والنوع والميزانية.", "/properties", "Search properties", "ابحث عن عقار"],
  [CalendarCheck, "Visit", "عاين", "Book an in-person or virtual viewing at a time that suits you.", "احجز معاينة حضورية أو افتراضية في الوقت المناسب لك.", "/schedule-viewing", "Schedule a viewing", "احجز معاينة"],
  [ShieldCheck, "Reserve", "احجز", "Our team guides you through reservation and paperwork.", "يرشدك فريقنا خلال إجراءات الحجز والوثائق.", "/contact", "Talk to our team", "تحدث مع فريقنا"],
  [KeyRound, "Own", "تملّك", "Receive the keys to your new home or investment — and our support continues.", "استلم مفاتيح منزلك أو استثمارك الجديد — ويستمر دعمنا لك.", "/contact", "Contact us", "تواصل معنا"],
];

export function Services() {
  const b = useBi();
  return (
    <section data-testid="home-services" className="relative bg-surface py-24">
      <div className="container-lux">
        <SectionHead eyebrow={b("Our Services", "خدماتنا")} title={b("Everything real estate, under one roof", "كل ما يتعلق بالعقار في مكان واحد")} />
        <div className="grid gap-px overflow-hidden rounded-sm border border-fg/10 bg-fg/10 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(([a, Icon, en, ar, den, dar], i) => (
            <Reveal key={a} delay={i * 0.05} className="bg-surface">
              <Link to={`/services#${a}`} data-testid={`home-service-${a}`} className="group flex h-full flex-col gap-6 p-8 transition-colors duration-300 hover:bg-surface-2">
                <div className="flex items-center justify-between"><Icon className="h-7 w-7 text-cta" strokeWidth={1.4} /><ArrowUpRight className="rtl-flip h-5 w-5 text-fg/30 transition-colors group-hover:text-cta" /></div>
                <div><h3 className="font-display text-2xl text-fg">{b(en, ar)}</h3><p className="mt-2 text-sm text-fg/60">{b(den, dar)}</p></div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function Why() {
  const b = useBi();
  return (
    <section data-testid="home-why" className="container-lux py-24">
      <div className="grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-4"><div className="lg:sticky lg:top-32"><SectionHead eyebrow={b("Why Landmark Future", "لماذا لاندمارك فيوتشر")} title={b("What we commit to", "ما نلتزم به")} sub={b("Our values guide every project we plan and every client we serve.", "قيمنا ترشدنا في كل مشروع نخطط له وكل عميل نخدمه.")} /></div></div>
        <div className="grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:col-span-8">
          {WHY.map(([Icon, en, ar, den, dar], i) => (
            <Reveal key={en} delay={i * 0.05} className="border-t border-cta/30 pt-6" >
              <div data-testid={`home-why-${i}`}>
                <Icon className="h-6 w-6 text-cta" strokeWidth={1.4} />
                <h3 className="mt-5 font-display text-2xl text-fg">{b(en, ar)}</h3>
                <p className="mt-2 text-sm leading-relaxed text-fg/60">{b(den, dar)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const b = useBi();
  const n = useNum();
  return (
    <section data-testid="home-how" className="relative overflow-hidden border-y border-cta/10 bg-surface py-24">
      <div className="container-lux relative">
        <SectionHead eyebrow={b("How it works", "كيف نعمل")} title={b("From first search to your keys", "من أول بحث حتى استلام المفاتيح")} />
        <ol className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {STEPS.map(([Icon, en, ar, den, dar, to, len, lar], i) => (
            <Reveal key={en} as="li" delay={i * 0.08} className="relative flex flex-col rounded-sm border border-fg/10 bg-canvas p-7">
              <span className="font-display text-6xl leading-none text-cta/25" aria-hidden="true">{n(i + 1).padStart(2, n(0))}</span>
              <Icon className="mt-6 h-6 w-6 text-cta" strokeWidth={1.4} />
              <h3 className="mt-4 font-display text-2xl text-fg">{b(en, ar)}</h3>
              <p className="mt-2 flex-1 text-sm text-fg/60">{b(den, dar)}</p>
              <Link to={to} data-testid={`home-how-${i}`} className="group mt-6 inline-flex items-center gap-2 text-sm text-cta">{b(len, lar)}<ArrowRight className="rtl-flip h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}
